import React, { useState, useEffect } from 'react';
import { BarChart3, Download, Calendar, Clock, User, FileText } from 'lucide-react';
import api from '../utils/api';

interface Quiz {
  id: string;
  title: string;
  subject: string;
}

interface Attempt {
  id: string;
  startTime: string;
  endTime: string;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  isOvertime: boolean;
  student: {
    name: string;
    email: string;
  };
  createdAt: string;
}

interface QuizAttemptsProps {
  quizzes: Quiz[];
  selectedQuiz: Quiz | null;
}

const QuizAttempts: React.FC<QuizAttemptsProps> = ({ quizzes, selectedQuiz }) => {
  const [selectedQuizId, setSelectedQuizId] = useState(selectedQuiz?.id || '');
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedQuizId) {
      fetchAttempts();
    }
  }, [selectedQuizId]);

  const fetchAttempts = async () => {
    if (!selectedQuizId) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/quizzes/${selectedQuizId}/attempts`);
      setAttempts(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch attempts');
    } finally {
      setLoading(false);
    }
  };

  const getSubjectColor = (subject: string) => {
    const colors = {
      VOCABULARY: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      CLOZE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      COMPREHENSION: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      MATHS: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
    };
    return colors[subject as keyof typeof colors] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const exportToCsv = () => {
    if (attempts.length === 0) return;

    const headers = [
      'Student Name',
      'Student Email',
      'Score (%)',
      'Correct Answers',
      'Total Questions',
      'Duration',
      'Overtime',
      'Start Time',
      'End Time'
    ];

    const rows = attempts.map(attempt => [
      attempt.student.name,
      attempt.student.email,
      Math.round(attempt.score),
      attempt.correctAnswers,
      attempt.totalQuestions,
      formatDuration(attempt.startTime, attempt.endTime),
      attempt.isOvertime ? 'Yes' : 'No',
      formatDateTime(attempt.startTime),
      formatDateTime(attempt.endTime)
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const selectedQuizTitle = quizzes.find(q => q.id === selectedQuizId)?.title || 'quiz';
    link.download = `${selectedQuizTitle.replace(/[^a-zA-Z0-9]/g, '_')}_attempts.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const calculateStats = () => {
    if (attempts.length === 0) return null;

    const scores = attempts.map(a => a.score);
    const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
    const highScore = Math.max(...scores);
    const lowScore = Math.min(...scores);
    const passRate = scores.filter(score => score >= 60).length / scores.length * 100;

    return {
      totalAttempts: attempts.length,
      uniqueStudents: new Set(attempts.map(a => a.student.email)).size,
      averageScore: Math.round(avgScore),
      highScore: Math.round(highScore),
      lowScore: Math.round(lowScore),
      passRate: Math.round(passRate)
    };
  };

  const stats = calculateStats();

  return (
    <div className="space-y-6">
      {/* Quiz Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Select Quiz to View Attempts</h3>

        {quizzes.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-300">No quizzes available.</p>
        ) : (
          <div className="grid gap-4">
            {quizzes.map((quiz) => (
              <label
                key={quiz.id}
                className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedQuizId === quiz.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="quiz"
                  value={quiz.id}
                  checked={selectedQuizId === quiz.id}
                  onChange={(e) => setSelectedQuizId(e.target.value)}
                  className="mr-4"
                />
                
                <div className="flex items-center space-x-3">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">{quiz.title}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSubjectColor(quiz.subject)}`}>
                    {quiz.subject}
                  </span>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Statistics */}
      {selectedQuizId && stats && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quiz Statistics</h3>
            <button
              onClick={exportToCsv}
              className="btn btn-outline text-sm"
              disabled={attempts.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.totalAttempts}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Total Attempts</p>
            </div>

            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.uniqueStudents}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Unique Students</p>
            </div>

            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.averageScore}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Average Score</p>
            </div>

            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.highScore}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Highest Score</p>
            </div>

            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{stats.lowScore}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Lowest Score</p>
            </div>

            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.passRate}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Pass Rate</p>
            </div>
          </div>
        </div>
      )}

      {/* Attempts List */}
      {selectedQuizId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Attempt History</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-300">Loading attempts...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              {error}
            </div>
          ) : attempts.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-300">No attempts found for this quiz</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Questions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Completed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {attempts.map((attempt) => (
                    <tr key={attempt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 dark:text-gray-500 mr-2" />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{attempt.student.name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{attempt.student.email}</p>
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <BarChart3 className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <p className={`font-bold ${getScoreColor(attempt.score)}`}>
                              {Math.round(attempt.score)}%
                            </p>
                            {attempt.isOvertime && (
                              <p className="text-xs text-orange-600">Overtime</p>
                            )}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {attempt.correctAnswers}/{attempt.totalQuestions}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatDuration(attempt.startTime, attempt.endTime)}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDateTime(attempt.endTime)}
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => window.open(`/quiz-results/${attempt.id}`, '_blank')}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizAttempts;